// Unpublished Work © 2022-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {DatePicker, Stack, TextField} from '@deere/isg.component-library';
import dayjs from 'dayjs';
import ValidationInput from 'Ui/components/common/form/validation-input';
import {onManualDataChange, validateDate} from 'Ui/components/manual-data/manual-data-utils';
import {useDeepMemo} from 'Utils/react-utils';
import {MANUAL_DATA_TIME_FORMAT} from 'Utils/time-utils';
import {getCurrencySymbol} from 'Utils/unit-conversion-utils';
import {isEmptyString, isNullOrUndefined} from 'Common/utils/validation-utils';
import {MAX_NUMERIC_VALUE, MIN_NUMERIC_VALUE} from 'Ui/constants/common-constants';
import {COUNT, DOLLARS} from 'Common/constants/data-unit-constants';

function valueOrEmptyString(value) {
    return isNullOrUndefined(value) ? '' : value;
}

function RevenueEntryForm(props) {
    const {
        membership,
        setValid,
        setValues,
        translations,
        values
    } = props;

    const [validatedDate, setValidatedDate] = React.useState();

    React.useEffect(() => {
        const validatedDateFromTime = validateDate(values.timeSample, setValid);

        setValidatedDate(validatedDateFromTime);
    }, [values.timeSample]);

    const {
        roundsCount,
        totalRevenue
    } = useDeepMemo(() => {
        const {manualData = [] } = values;

        const manualDataMap = manualData.reduce((manualDataByType, manualData) => {
            manualDataByType.set(manualData.data_type, manualData);

            return manualDataByType;
        }, new Map());

        const roundsCount = manualDataMap.get('rounds');
        const totalRevenue = manualDataMap.get('total_revenue');

        return {
            roundsCount: valueOrEmptyString(roundsCount?.value),
            totalRevenue: valueOrEmptyString(totalRevenue?.value)
        };
    }, [values.manualData]);

    const isInvalid = isEmptyString(roundsCount) && isEmptyString(totalRevenue);

    function onValuesChange(name, value) {
        setValues((prevValues) => ({
            ...prevValues,
            [name]: value
        }));
    }

    const currencySymbol = getCurrencySymbol({
        currencyPreference: membership.currencyPreference
    });

    const sharedInputProps = {
        debounceTimeout: window.props.debounceTimeout,
        errors: {
            rangeOverflow: `${translations.ONLINK_VALUE_LESS_THAN_OR_EQUAL} ${MAX_NUMERIC_VALUE}`,
            rangeUnderflow: `${translations.ONLINK_VALUE_GREATER_THAN_OR_EQUAL} ${MIN_NUMERIC_VALUE}`
        },
        invalidCustomError: isInvalid,
        max: MAX_NUMERIC_VALUE,
        min: MIN_NUMERIC_VALUE,
        required: true,
        setValid,
        tabIndex: 0,
        type: 'number'
    };

    return (
        <div className='settings-body manual-data-form'>
            <DatePicker
                helperText={validatedDate ? '' : translations.INVALIDATED_DATE}
                label={translations.DATE}
                onChange={(date) => onValuesChange('timeSample', dayjs(date).format(MANUAL_DATA_TIME_FORMAT))}
                value={validatedDate?.toDate() || new Date()}
            />
            <Stack
                direction='row'
                spacing={4}
                sx={{
                    width: '100%',
                    '& > *': {
                        flex: 1,
                        minWidth: 0
                    }
                }}
            >
                <ValidationInput
                    {...sharedInputProps}
                    fullWidth={true}
                    label={`${translations.Rounds} (${translations.ONLINK_COUNT})`}
                    name='rounds'
                    onChange={(event) => onManualDataChange({
                        dataUnit: COUNT,
                        name: event.target.name,
                        setValues,
                        value: event.target.value
                    })}
                    value={roundsCount}
                />
                <ValidationInput
                    {...sharedInputProps}
                    fullWidth={true}
                    label={`${translations.ONLINK_TOTAL_REVENUE} (${currencySymbol})`}
                    name='total_revenue'
                    onChange={(event) => onManualDataChange({
                        dataUnit: DOLLARS,
                        name: event.target.name,
                        setValues,
                        value: event.target.value
                    })}
                    value={totalRevenue}
                />
            </Stack>
            <TextField
                label={translations.NOTES}
                multiline
                name='note'
                onChange={(event) => onValuesChange('note', event.target.value)}
                tabIndex={0}
                value={values.note || ''}
            />
        </div>
    );
}

RevenueEntryForm.propTypes = {
    membership: PropTypes.membership,
    setValid: PropTypes.func,
    setValues: PropTypes.func,
    translations: PropTypes.translations,
    values: PropTypes.object
};

export default RevenueEntryForm;
